import React, { useEffect } from "react";
import { useLanguage } from "../../i18n/LanguageContext";

// Renders a legal document from src/content/legal.js. The document text is
// shown exactly as written; this component only adds layout, a contents list
// and mailto links on email addresses (the visible text is unchanged).

const EMAIL = /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;

const withEmailLinks = (text, keyPrefix) =>
  text.split(EMAIL).map((part, i) =>
    i % 2 === 1 ? (
      <a
        key={`${keyPrefix}-${i}`}
        href={`mailto:${part}`}
        className="text-[#E8490F] underline decoration-[#E8490F]/40 underline-offset-4 hover:decoration-[#E8490F]"
      >
        {part}
      </a>
    ) : (
      part
    )
  );

const Runs = ({ runs, boldClass = "text-white font-semibold" }) =>
  runs.map(([text, bold], i) =>
    bold ? (
      <strong key={i} className={boldClass}>
        {withEmailLinks(text, i)}
      </strong>
    ) : (
      <React.Fragment key={i}>{withEmailLinks(text, i)}</React.Fragment>
    )
  );

const plain = (runs) => runs.map(([text]) => text).join("");
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const LegalDocument = ({ blocks, pageTitle, testId }) => {
  const { lang } = useLanguage();

  useEffect(() => {
    const prev = document.title;
    document.title = pageTitle;
    return () => {
      document.title = prev;
    };
  }, [pageTitle]);

  const titles = blocks.filter((b) => b.type === "title");
  const effective = blocks.find((b) => b.type === "effective");
  const body = blocks.filter((b) => b.type !== "title" && b.type !== "effective");
  const sections = body
    .filter((b) => b.type === "heading" || b.type === "subheading")
    .map((b) => ({ id: slug(plain(b.runs)), label: plain(b.runs), number: b.number }));

  return (
    <div data-testid={testId}>
      <section className="border-b border-[#242424] relative overflow-hidden">
        <div
          className="pointer-events-none absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-[#E8490F]/10 blur-3xl"
          aria-hidden
        />
        <div className="mise-container pt-12 md:pt-16 pb-12 md:pb-14">
          <p className="mise-eyebrow">{lang === "fr" ? "Mentions légales" : "Legal"}</p>
          <h1 className="mt-5 text-4xl md:text-5xl font-semibold tracking-tight leading-[1.1]">
            {titles.map((b, i) => (
              <span key={i} className={i === titles.length - 1 && titles.length > 1 ? "block mise-serif mise-accent font-normal" : "block"}>
                {plain(b.runs)}
              </span>
            ))}
          </h1>
          {effective && (
            <p className="mt-6 text-white/60 leading-relaxed max-w-2xl">
              <Runs runs={effective.runs} />
            </p>
          )}
          {lang === "fr" && (
            <p className="mt-3 text-sm text-white/45" data-testid="legal-english-only">
              Ce document est offert en anglais seulement.
            </p>
          )}
        </div>
      </section>

      <section className="mise-section">
        <div className="mise-container grid lg:grid-cols-12 gap-10">
          {sections.length > 0 && (
            <nav className="lg:col-span-3 hidden lg:block" aria-label={lang === "fr" ? "Sommaire" : "Contents"}>
              <div className="sticky top-28">
                <p className="mise-eyebrow mb-4">{lang === "fr" ? "Sommaire" : "Contents"}</p>
                <ol className="space-y-2 text-sm">
                  {sections.map((s) => (
                    <li key={s.id}>
                      <a href={`#${s.id}`} className="flex gap-2 text-white/55 hover:text-white transition-colors">
                        {s.number && <span className="text-[#E8490F] tabular-nums">{s.number}</span>}
                        <span>{s.label}</span>
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            </nav>
          )}

          <article className="lg:col-span-8 lg:col-start-5 max-w-3xl">
            {body.map((b, i) => {
              if (b.type === "heading" || b.type === "subheading") {
                const id = slug(plain(b.runs));
                return (
                  <h2
                    key={i}
                    id={id}
                    className="scroll-mt-28 mt-12 first:mt-0 pt-8 first:pt-0 border-t border-[#242424] first:border-t-0 text-xl md:text-2xl font-semibold text-white flex gap-3"
                  >
                    {b.number && <span className="text-[#E8490F] tabular-nums">{b.number}</span>}
                    <span>{plain(b.runs)}</span>
                  </h2>
                );
              }
              if (b.type === "numbered") {
                return (
                  <div key={i} className="mt-5 flex gap-4 text-white/75 leading-relaxed">
                    <span className="text-[#E8490F] font-semibold tabular-nums">{b.number}</span>
                    <p>
                      <Runs runs={b.runs} />
                    </p>
                  </div>
                );
              }
              return (
                <p key={i} className="mt-5 text-white/75 leading-relaxed break-words">
                  <Runs runs={b.runs} />
                </p>
              );
            })}
          </article>
        </div>
      </section>
    </div>
  );
};

export default LegalDocument;
